import { useMemo } from "react"

import { useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"
import type { SolarProposalDesignReadDto } from "~/api/dto/solar-proposal-design.dto"
import type { SolarProposalPricingDto } from "~/api/dto/solar-proposal-pricing.dto"
import type { SolarProposalSavingsSectionDto } from "~/api/dto/solar-proposal-savings.dto"
import useCalculateSolarProposalPricing from "~/api/hooks/useCalculateSolarProposalPricing"
import useRecalculateSolarProposalSavings from "~/api/hooks/useRecalculateSolarProposalSavings"
import useUpdateSolarProduction from "~/api/hooks/useUpdateSolarProduction"

import {
  describeSolarUpdateError,
  isSolarUpdateNeeded,
  isSolarUpdateRunnable,
  SOLAR_LIGHTREACH_PRICING_UPDATE_MESSAGE,
  SOLAR_UPDATES_SPEC,
  solarLightReachPricingFix,
  solarUpdateFailureFix,
  type SolarUpdateKey,
  type SolarUpdateReads,
} from "./solar-updates"
import {
  UPDATES_SETTLE_MS,
  type UpdatesRunner,
  type UpdatesRunnerConfig,
  useUpdatesRunner,
} from "../ProposalPresentation/updates/use-updates-runner"

/** How long fixed rows stay green before the panel leaves. */
export const SOLAR_UPDATES_SETTLE_MS = UPDATES_SETTLE_MS

export type SolarUpdatesRunner = UpdatesRunner<SolarUpdateKey>

/**
 * The Solar Updates panel's runner, on the existing commands: Retry
 * production, Refresh Pricing and Recalculate savings, in production →
 * pricing → savings order (pricing reads production; savings read both). A
 * savings run after a price refresh skips net savings the new price made
 * current.
 */
export const useSolarUpdatesRunner = ({
  proposalId,
  settled,
  priceSetByOffer = false,
}: {
  proposalId: string
  /** Nothing is out of date any more: fixed rows clear after a moment. */
  settled: boolean
  /** A LightReach plan: its pricing is Loan Product's, never run here. */
  priceSetByOffer?: boolean
}): SolarUpdatesRunner => {
  const queryClient = useQueryClient()
  const { mutateAsync: retryProduction } = useUpdateSolarProduction()
  const { mutateAsync: refreshPricing } = useCalculateSolarProposalPricing()
  const { mutateAsync: recalculateSavings } =
    useRecalculateSolarProposalSavings()

  const config = useMemo((): UpdatesRunnerConfig<
    SolarUpdateKey,
    SolarUpdateReads
  > => {
    const readKeys = {
      production: queryKeys.solarProposal.design(proposalId),
      pricing: queryKeys.solarProposal.pricing(proposalId),
      savings: queryKeys.solarProposal.savings(proposalId),
    }
    return {
      spec: SOLAR_UPDATES_SPEC,
      readKeys,
      readsNow: () => ({
        design: queryClient.getQueryData<SolarProposalDesignReadDto>(
          readKeys.production,
        ),
        pricing: queryClient.getQueryData<SolarProposalPricingDto>(
          readKeys.pricing,
        ),
        savings: queryClient.getQueryData<SolarProposalSavingsSectionDto>(
          readKeys.savings,
        ),
        priceSetByOffer,
      }),
      isNeeded: isSolarUpdateNeeded,
      unrunnable: (key) =>
        isSolarUpdateRunnable(key, { priceSetByOffer })
          ? null
          : {
              message: SOLAR_LIGHTREACH_PRICING_UPDATE_MESSAGE,
              fix: solarLightReachPricingFix(proposalId),
            },
      steps: {
        production: () => retryProduction({ proposalId }),
        pricing: () => refreshPricing({ proposalId }),
        savings: () => recalculateSavings({ proposalId }),
      },
      describeFailure: (key, error) => ({
        message: describeSolarUpdateError(key, error),
        fix: solarUpdateFailureFix(key, error, proposalId),
      }),
    }
  }, [
    priceSetByOffer,
    proposalId,
    queryClient,
    recalculateSavings,
    refreshPricing,
    retryProduction,
  ])

  return useUpdatesRunner({ config, settled })
}
